import { LAYER_KEYS, type LayerKey } from './layer-preferences';
import type { SavedProfileView } from './saved-views';

/**
 * Curated SEAT PROFILE seeds (#187 — saved seat role-profiles).
 *
 * A seat profile is a named bundle of layer-preference DELTAS + a panel
 * arrangement so an operator's screens come up configured for their IMS seat.
 * These four seeds cover the doctrinal functions TSR most directly serves
 * (operator-cop-doctrine §2.4 Situation Unit, §2.5 Logistics, the Liaison /
 * single-point-of-contact thread §2.2, plus the Municipal EOC seat — role-view
 * council §2, docs/role-view-council-2026-06.md) — they are CODE constants, not
 * DB rows; only operator-saved custom profiles persist (operator_saved_views
 * kind='profile', migration 0066).
 *
 * DELTAS, NEVER ABSOLUTE SETS: each seed lists only the layer keys that differ
 * from the locked default view (#202 — DEFAULT_VIEW_LAYERS + defaultLayerOn).
 * Applying a profile = the locked defaults + these deltas, so:
 *   - a fresh session with no profile applied boots to DEFAULT_VIEW_LAYERS
 *     exactly (profiles cannot fossilize or fork the baseline);
 *   - all four seeds inherit the default 4 (Live Video Feeds · Aircraft &
 *     Marine · Reported OC Wildfires · Urgent Alerts) plus the documented
 *     non-view chrome (EWW mute un-muted, sectors, provincial boundaries) —
 *     none of that is repeated here.
 *
 * Per-operator preference ONLY — this is workspace configuration, NOT an auth
 * model. Lead-assignment / RBAC is explicitly out of scope (tracker #187 note).
 */
export interface SeatProfileSeed {
  /** Stable slug id, namespaced so it can never collide with a DB row uuid. */
  id: string;
  /** Picker label — the IMS seat name. */
  label: string;
  /** One-line "what this seat sees" hint shown in the picker. */
  description: string;
  /** Layer-key deltas over the locked default view (true = turn ON). Typed
   *  against the LayerKey union so a layer rename fails the build here, and the
   *  pinned test below catches it at runtime too. */
  layerDeltas: Partial<Record<LayerKey, boolean>>;
}

export const SEAT_PROFILE_SEEDS: readonly SeatProfileSeed[] = [
  {
    id: 'seed:situation-unit',
    label: 'Situation Unit',
    description: 'Collect → confirm → analyze: pre-fusion signals + full fire picture',
    // §2.4 — the SITL owns the COP and runs collect→confirm→analyze→share. The
    // seat needs every incoming signal and its corroboration context, not just
    // the fused output: raw satellite thermal + camera-CV smoke are the
    // confirm-phase inputs; the all-stages wildfire picture + FWI conditions are
    // the analyze context. Asset/logistics layers stay OFF — resourcing is not
    // this seat's question. (EWW + sectors are already default-ON chrome.)
    layerDeltas: {
      'raw-hotspots': true,
      'camera-smoke-detections': true,
      'all-reported-wildfires': true,
      'fire-weather-index': true,
    },
  },
  {
    id: 'seed:logistics',
    label: 'Logistics',
    description: 'Resource inventory, staging areas and movement corridors',
    // §2.5 — Logistics sources, orders and tracks resources, manages staging and
    // transport. The seat needs the inventory substrate (every response-asset
    // sublayer + the Ontario Corps partner registry), the staging-area depots
    // (the lifecycle's `staging` state), drone-operator coverage (a sourcing
    // option), and road closures (whether the ground corridor is open is a pure
    // logistics question). Pre-fusion fire-risk noise stays OFF — confirmation
    // is the Situation Unit's job, not this seat's.
    layerDeltas: {
      'staging-areas': true,
      'fire-stations': true,
      'wildfire-bases': true,
      'sar-aviation': true,
      'ontario-corps': true,
      'drone-operator-coverage': true,
      'ice-storm-road-closures': true,
    },
  },
  {
    id: 'seed:liaison',
    label: 'Liaison',
    description: 'What other organizations have declared, ordered and need',
    // Liaison (the cross-org / single-point-of-contact thread, §2.2) — the seat
    // works the seams between organizations, so it needs the "what has another
    // org declared or ordered" picture: evacuation orders/alerts, States of
    // Local Emergency + Band Council Resolutions, boil-water advisories, the
    // vulnerable-community layer under both hazard groups (who may need to be
    // engaged), and power outages (the most common cross-agency ask). Pre-fusion
    // detection layers stay OFF — Liaison consumes the confirmed picture.
    layerDeltas: {
      'bc-evacuation-orders': true,
      'bc-state-of-local-emergency': true,
      'boil-water-advisories': true,
      'flood-vulnerable-communities': true,
      'ice-storm-vulnerable-communities': true,
      'ice-storm-power-outages': true,
    },
  },
  {
    id: 'seed:municipal-eoc',
    label: 'Municipal EOC',
    description: 'Local operational COP: communities, declarations, outages, roads, gauges and orders',
    // Municipal EOC (role-view council §2 — docs/role-view-council-2026-06.md,
    // ratified by the Municipal seat). The town / First-Nation operator's seat.
    // This is role-aware DEFAULTS over the SAME locked baseline, NOT a fork: the
    // four DEFAULT_VIEW_LAYERS (Urgent Alerts · Reported OC Wildfires · Live Video
    // · Aircraft & Marine — the spine works for this seat too) stay ON, inherited
    // and NOT restated here; the province-wide PEOC analytics (FWI heatmap /
    // Fusion Candidates / Hot Spots / aviation inventory / Tropical Cyclones …)
    // stay OFF, also inherited (no delta needed — they're default-OFF). The deltas
    // turn ON exactly the local operational picture the seat is measured on: the
    // vulnerable-community object under both hazard groups (the load-bearing
    // wellness/headcount layer — the named 2025 ice-storm failure), the municipal
    // declaration/advisory layers (State of Local Emergency + Band Council
    // Resolutions, boil-water), the comms-degradation ESTIMATE (the operator's own
    // blindness when towers drop), power outages + road closures (a town
    // operator's info-priority #1/#2), hydrometric gauges (river rise — flood
    // country), and evacuation orders/alerts affecting their people. Every one of
    // these self-gates to regional+ zoom, so no national-scale clutter. The exact
    // delta set is pinned in seat-profiles.test.ts (council §5.3 — a future change
    // to the municipal layer set is an explicit, reviewed edit).
    layerDeltas: {
      'flood-vulnerable-communities': true,
      'ice-storm-vulnerable-communities': true,
      'bc-state-of-local-emergency': true,
      'boil-water-advisories': true,
      'ice-storm-comms-degradation': true,
      'ice-storm-power-outages': true,
      'ice-storm-road-closures': true,
      'hydrometric-gauges': true,
      'bc-evacuation-orders': true,
    },
  },
];

/** The Municipal EOC seed id — the seat the PEOC "view as <community>" transient
 *  lens glances through (role-view council §3, docs/role-view-council-2026-06.md). */
export const MUNICIPAL_EOC_SEED_ID = 'seed:municipal-eoc';

/** Seed ids, for "is this a seed or a custom profile" checks. */
export function isSeatProfileSeedId(id: string): boolean {
  return SEAT_PROFILE_SEEDS.some((s) => s.id === id);
}

/** A seed's layer deltas by id (a fresh copy), or null if no seed has that id.
 *  Used by the role→seed boot scaffold and the municipal drill-in lens to resolve
 *  a seed id to the deltas they apply over the locked default view. */
export function seatSeedDeltasById(id: string): Partial<Record<LayerKey, boolean>> | null {
  const seed = SEAT_PROFILE_SEEDS.find((s) => s.id === id);
  return seed ? { ...seed.layerDeltas } : null;
}

/** A seed's deltas as a plain SavedProfileView (no panel arrangement — seeds
 *  configure layers only; pins are personal). */
export function seedToProfileView(seed: SeatProfileSeed): SavedProfileView {
  return { layerDeltas: { ...seed.layerDeltas } as Record<string, boolean> };
}

/** Runtime guard used by the pinned seed test: every delta key of every seed
 *  must be a member of the LayerKey union (a layer rename must not silently
 *  break a seed). */
export function invalidSeedLayerKeys(): string[] {
  const known = new Set<string>(LAYER_KEYS);
  return SEAT_PROFILE_SEEDS.flatMap((s) =>
    Object.keys(s.layerDeltas).filter((k) => !known.has(k)),
  );
}
